import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { joiResolver } from "@hookform/resolvers/joi";
import useAuth from "../../../../hooks/useAuth";
import BsInputPassword from "../../../public/BsInputPassword";
import { useTranslation } from "react-i18next";
import { ModalShell } from "../../../atoms/ModalShell";
import { BiCheck, BiX } from "react-icons/bi";
import PasswordValidator from "../../../public/PasswordValidator";
import { CustomButton } from "../../../atoms/CustomButton";
import { useCheckPassword } from "../../../../hooks/auth";
import { useUserUpdatePassword } from "../../../../hooks/users";
import {
  meetsPasswordRules,
  usePasswordRules,
} from "../../../../hooks/usePasswordResetContext";
import {
  changePasswordSchema,
  shownFieldError,
} from "../../../../utils/validationPatterns";

const ProfilePasswordChange = () => {
  const { t } = useTranslation();

  const { customer_identification, user } = useAuth();

  const [show, setShow] = useState(false);
  const [apiError, setApiError] = useState<any>({});
  const [validatorCurrentPassword, setValidatorCurrentPassword] =
    useState<string>("");

  const { passwordLevel, passwordRules, acceptedLevels } = usePasswordRules(
    user?.entity_identification ?? null
  );

  const activeRuleSet = useMemo(
    () =>
      passwordRules.map((rule) => ({
        regex: rule.regex,
        message: rule.description,
      })),
    [passwordRules]
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: joiResolver(changePasswordSchema),
  });

  const { mutate: checkPassword } = useCheckPassword();
  const { mutate: updatePassword, isLoading } = useUserUpdatePassword();

  const onSubmit = (values: any) => {
    checkPassword(
      {
        password: values.new_password,
        ...(passwordLevel ? { level: passwordLevel } : {}),
      },
      {
        onSuccess: () => {
          updatePassword(
            {
              customer_identification: customer_identification,
              entityIdentification: user.entity_identification,
              data: {
                actual_password: values.actual_password,
                new_password: values.new_password,
              },
            },
            {
              onSuccess: () => {
                setApiError(null);
                reset();
                setValidatorCurrentPassword("");
                setShow(false);
              },
              onError: (err) => {
                setApiError(err.response?.data.element);
              },
            }
          );
        },
      }
    );
  };

  return (
    <div className="px-5 pt-5 pb-8 bg-white rounded-lg border border-gray-200 flex-col justify-start items-start gap-5 inline-flex">
      <div className="self-stretch h-14 flex-col justify-start items-start gap-2 flex">
        <div className="text-gray-700 text-lg font-semibold font-figtree leading-7">
          {t("dashboard.profile.key9")}
        </div>
        <div className="text-gray-700 text-sm font-normal font-figtree leading-tight">
          {t("dashboard.profile.key10")}
        </div>
      </div>
      <div className="w-full flex-col justify-start items-start gap-2 flex">
        <div className="w-full justify-start items-start gap-3 flex flex-wrap">
          <div className="justify-start items-start gap-5 flex">
            <div className="md:w-[318px] w-full flex-col justify-start items-start inline-flex">
              <div className="self-stretch px-3 py-2 bg-white rounded-md shadow border border-gray-300 justify-start items-center inline-flex">
                <div className="text-gray-700 text-sm font-normal font-figtree leading-tight">
                  •••••••••••
                </div>
              </div>
            </div>
          </div>
          <CustomButton
            variant="primary"
            type="submit"
            onClick={() => setShow(true)}
            title={t("dashboard.profile.key11")}
          />
        </div>
      </div>

      <ModalShell
        show={show}
        onClose={() => setShow(false)}
        size={"md"}
        title={t("dashboard.profile.key11")}
        onSubmit={handleSubmit(onSubmit)}
        footer={
          <>
            <CustomButton
              onClick={() => setShow(false)}
              title={t("common.cancel")}
            />
            <CustomButton
              type="submit"
              variant="primary"
              disabled={isLoading}
              isLoading={isLoading}
              title={t("common.update")}
            />
          </>
        }
      >
        <div>
          <BsInputPassword
            label={
              t("dashboard.userProfile.changePasswordForm.label-01") as string
            }
            isInvalid={!!errors.actual_password || !!apiError?.actual_password}
            errorMessage={
              shownFieldError(t, errors.actual_password?.message as string) ||
              apiError?.actual_password
            }
            className={"w-full mb-4"}
            rhfControls={register("actual_password")}
          />

          <BsInputPassword
            label={
              t("dashboard.userProfile.changePasswordForm.label-02") as string
            }
            isInvalid={!!errors.new_password || !!apiError?.new_password}
            errorMessage={
              shownFieldError(t, errors.new_password?.message as string) ||
              apiError?.new_password
            }
            className={`w-full ${
              validatorCurrentPassword.length > 0 ? "mb-2" : "mb-4"
            }`}
            rhfControls={register("new_password", {
              onChange(event) {
                setValidatorCurrentPassword(event.target.value);
              },
            })}
          />
          <PasswordValidator
            className={
              validatorCurrentPassword.length > 0 ? "mb-3 text-xs" : "hidden"
            }
            ruleSet={activeRuleSet}
            passwordValue={validatorCurrentPassword}
            passwordAccepted={meetsPasswordRules(
              validatorCurrentPassword,
              { level: passwordLevel ?? "", rules: passwordRules },
              acceptedLevels
            )}
            passedRuleClassName="flex items-center text-green-500"
            failedRuleClassName="flex items-center text-red-500"
            passIcon={<BiCheck size={16} />}
            failIcon={<BiX size={16} />}
          />

          <BsInputPassword
            label={
              t("dashboard.userProfile.changePasswordForm.label-03") as string
            }
            isInvalid={!!errors.new_confirm_password}
            errorMessage={shownFieldError(
              t,
              errors.new_confirm_password?.message as string
            )}
            className={"w-full mb-4"}
            rhfControls={register("new_confirm_password")}
          />
        </div>
      </ModalShell>
    </div>
  );
};

export default ProfilePasswordChange;
